import React, { useCallback, useRef, useState } from "react";
import { Upload, X, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";
import type { AdminProductItem } from "../hooks/useAdminProductsList";
import { METADATA_FIELD_CONFIGS } from "../../../../types/productMetadata";
import { adminProductsV3Service } from "../../../../services/adminProductsV3Service";
import {
  mergeCsvMetadata,
  parseCsvFile,
  type CsvRow,
  type CsvValidationError,
} from "../utils/metadataCsv";

interface CsvUploadReviewProps {
  products: AdminProductItem[];
  onComplete: () => void;
  onCancel: () => void;
}

type UploadPhase = "idle" | "review" | "saving" | "done";

const FIELD_KEYS = METADATA_FIELD_CONFIGS.map((c) => c.key);

const CsvUploadReview: React.FC<CsvUploadReviewProps> = ({
  products,
  onComplete,
  onCancel,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<UploadPhase>("idle");
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [errors, setErrors] = useState<CsvValidationError[]>([]);
  const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 });
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const text = await file.text();
      const result = parseCsvFile(text, products);
      setRows(result.rows);
      setErrors(result.errors);
      setPhase("review");

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [products]
  );

  const handleSave = useCallback(async () => {
    if (rows.length === 0) return;
    setPhase("saving");
    setSaveProgress({ done: 0, total: rows.length });
    const failedRows: string[] = [];
    const productLookup = new Map(
      products.map((product) => [
        `${product.productType}-${product.id}-${product.providerId ?? ""}`,
        product,
      ])
    );

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const productType = row.product_type;
      const productId = Number(row.product_id);
      const providerId = row.provider_id === null ? null : Number(row.provider_id);
      const product = productLookup.get(
        `${productType}-${row.product_id}-${row.provider_id ?? ""}`
      );

      if (!product) {
        failedRows.push(`${row.product_type} #${row.product_id}: Product no longer exists`);
        setSaveProgress({ done: i + 1, total: rows.length });
        continue;
      }

      const metadata = mergeCsvMetadata(product.settings, row.tags);

      try {
        await adminProductsV3Service.patchProductMetadata(
          productType,
          providerId,
          productId,
          metadata
        );
      } catch (err) {
        failedRows.push(`${row.product_type} #${row.product_id}: ${err instanceof Error ? err.message : "Unknown error"}`);
      }
      setSaveProgress({ done: i + 1, total: rows.length });
    }

    setSaveErrors(failedRows);
    setPhase("done");
  }, [products, rows]);

  const hasBlockingErrors = errors.length > 0;
  const validRowCount = rows.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-rp-dark-grey border border-rp-separator rounded-2xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-rp-separator">
          <h2 className="text-sm font-bold text-rp-grey">
            {phase === "idle" && "Upload Metadata CSV"}
            {phase === "review" && "Review Changes"}
            {phase === "saving" && "Saving..."}
            {phase === "done" && "Complete"}
          </h2>
          {phase !== "saving" && (
            <button
              onClick={phase === "done" ? onComplete : onCancel}
              className="text-rp-grey/60 hover:text-rp-grey"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* Idle: file picker */}
          {phase === "idle" && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Upload className="w-10 h-10 text-rp-grey/40" />
              <p className="text-xs text-rp-grey/60">
                Upload a CSV with the same format as the download.
              </p>
              <label className="px-4 py-2 rounded-lg text-xs font-medium bg-rp-amber text-black cursor-pointer hover:bg-rp-amber/80 transition-colors">
                Choose File
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>
          )}

          {/* Review */}
          {phase === "review" && (
            <div className="space-y-4">
              {/* Errors */}
              {errors.length > 0 && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-xs font-bold text-red-400">
                      {errors.length} validation error{errors.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="max-h-40 overflow-auto space-y-1">
                    {errors.map((err, i) => (
                      <p key={i} className="text-[10px] text-red-300 font-mono">
                        Row {err.row}{err.column ? `, ${err.column}` : ""}: {err.message}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Valid rows preview */}
              {validRowCount > 0 && (
                <div className="rounded-lg border border-rp-separator bg-rp-black p-4">
                  <p className="text-xs font-bold text-rp-green mb-3">
                    {validRowCount} product{validRowCount !== 1 ? "s" : ""} will be updated
                  </p>
                  <div className="overflow-auto max-h-60">
                    <table className="w-full text-[10px] text-rp-grey border-separate border-spacing-0">
                      <thead>
                        <tr>
                          <th className="text-left px-2 py-1 border-b border-rp-separator font-bold">Type</th>
                          <th className="text-left px-2 py-1 border-b border-rp-separator font-bold">ID</th>
                          <th className="text-left px-2 py-1 border-b border-rp-separator font-bold">Title</th>
                          {FIELD_KEYS.map((k) => (
                            <th key={k} className="text-center px-2 py-1 border-b border-rp-separator font-bold">
                              {METADATA_FIELD_CONFIGS.find((c) => c.key === k)?.label ?? k}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1">
                              {row.product_type === "basket"
                                ? "B"
                                : row.product_type === "portfolio"
                                ? "P"
                                : "M"}
                            </td>
                            <td className="px-2 py-1 font-mono">{row.product_id}</td>
                            <td className="px-2 py-1 truncate max-w-[120px]" title={row.title}>
                              {row.title}
                            </td>
                            {FIELD_KEYS.map((k) => (
                              <td key={k} className="px-2 py-1 text-center">
                                {(row.tags as any)[k] ?? "--"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Saving */}
          {phase === "saving" && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-rp-amber" />
              <p className="text-xs text-rp-grey">
                Saving {saveProgress.done} / {saveProgress.total}...
              </p>
              <div className="w-64 h-1.5 bg-rp-separator rounded-full overflow-hidden">
                <div
                  className="h-full bg-rp-amber transition-all"
                  style={{ width: `${(saveProgress.done / saveProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Done */}
          {phase === "done" && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <CheckCircle className="w-8 h-8 text-rp-green" />
              <p className="text-xs text-rp-grey">
                Updated {saveProgress.total - saveErrors.length} of {saveProgress.total} products.
              </p>
              {saveErrors.length > 0 && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 w-full max-w-md">
                  <p className="text-[10px] font-bold text-red-400 mb-1">
                    {saveErrors.length} failed:
                  </p>
                  {saveErrors.map((e, i) => (
                    <p key={i} className="text-[10px] text-red-300 font-mono">{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-rp-separator">
          {phase === "review" && (
            <>
              <button
                onClick={() => { setPhase("idle"); setRows([]); setErrors([]); }}
                className="px-4 py-1.5 rounded-lg text-xs font-medium text-rp-grey bg-rp-separator hover:bg-rp-separator/80 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSave}
                disabled={hasBlockingErrors || validRowCount === 0}
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-rp-amber text-black hover:bg-rp-amber/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Save {validRowCount} product{validRowCount !== 1 ? "s" : ""}
              </button>
            </>
          )}
          {phase === "done" && (
            <button
              onClick={onComplete}
              className="px-4 py-1.5 rounded-lg text-xs font-medium bg-rp-amber text-black hover:bg-rp-amber/80 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvUploadReview;
